import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { useLanguage } from '../contexts/LanguageContext';
import { Shirt, Calendar, Sparkles } from 'lucide-react';

export const DressCode = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen cream-bg">
      <Navbar />
      
      {/* Header Section */}
      <section className="pt-56 md:pt-48 pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Shirt Icon */}
          <div className="mb-8">
            <svg 
              width="48" 
              height="48" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="#335B7A" 
              strokeWidth="1.5" 
              strokeLinecap="round" 
              strokeLinejoin="round"
              style={{ display: 'block', margin: '0 auto' }}
            >
              <path d="M20.38 3.46L16 2a4 4 0 01-8 0L3.62 3.46a2 2 0 00-1.34 2.23l.58 3.47a1 1 0 00.99.84H6v10c0 1.1.9 2 2 2h8a2 2 0 002-2V10h2.15a1 1 0 00.99-.84l.58-3.47a2 2 0 00-1.34-2.23z"/>
            </svg>
          </div>
          <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl font-semibold text-foreground mb-6">
            {t('schedule.dressCode.title')}
          </h1>
          <p className="font-sans text-base md:text-xl text-muted-foreground max-w-3xl mx-auto">
            {t('schedule.dressCode.intro')}
          </p>
        </div>
      </section>

      {/* Dress Code Content */}
      <section className="pb-20 cream-bg">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {/* Sunday - Welcome Dinner */}
            <div className="border-2 border-primary cream-bg p-6 md:p-8">
              <div className="flex items-start gap-4 mb-4">
                <Calendar className="w-6 h-6 blue-text flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h3 className="font-serif text-2xl font-semibold blue-text mb-2">
                    {t('schedule.dressCode.sunday')}
                  </h3>
                  <p className="font-sans text-base blue-text opacity-80 mb-3">
                    {t('schedule.dressCode.sundayDesc')}
                  </p>
                  <div className="inline-block px-4 py-2 border-2 border-primary cream-bg">
                    <p className="font-sans font-semibold blue-text text-sm">
                      {t('schedule.dressCode.sundayCode')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Monday - Wedding Day */}
            <div className="border-2 border-primary blue-bg p-6 md:p-8">
              <div className="flex items-start gap-4 mb-4">
                <Sparkles className="w-6 h-6 text-white flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h3 className="font-serif text-2xl font-semibold text-white mb-2">
                    {t('schedule.dressCode.monday')}
                  </h3>
                  <p className="font-sans text-base text-white opacity-90 mb-4">
                    {t('schedule.dressCode.mondayDesc')}
                  </p>
                  <div className="cream-bg border-2 border-white p-4 mb-4">
                    <p className="font-sans font-semibold blue-text text-sm mb-3">
                      {t('schedule.dressCode.mondayCode')}
                    </p>
                    <p className="font-sans text-sm blue-text opacity-80 mb-2">
                      {t('schedule.dressCode.mondayNote')}
                    </p>
                  </div>
                  
                  <div className="space-y-3 text-white">
                    <div className="cream-bg border-2 border-white p-4">
                      <p className="font-sans font-semibold blue-text mb-2">{t('schedule.dressCode.ladies')}</p>
                      <p className="font-sans text-sm blue-text opacity-80">
                        {t('schedule.dressCode.ladiesDesc')}
                      </p>
                    </div>
                    
                    <div className="cream-bg border-2 border-white p-4">
                      <p className="font-sans font-semibold blue-text mb-2">{t('schedule.dressCode.gentlemen')}</p>
                      <p className="font-sans text-sm blue-text opacity-80 mb-2">
                        {t('schedule.dressCode.gentlemenDesc')}
                      </p>
                      <p className="font-sans text-xs blue-text opacity-60 italic">
                        {t('schedule.dressCode.tuxedoNote')}
                      </p>
                    </div>
                  </div>
                  
                  {/* Detailed Dress Code Description */}
                  <div className="cream-bg border-2 border-white p-6 mt-4">
                    <div className="space-y-4 font-sans text-sm blue-text leading-relaxed">
                      <p className="font-bold text-base">{t('schedule.dressCode.detailTitle')}</p>
                      
                      <p>{t('schedule.dressCode.detailIntro')}</p>
                      
                      <p>{t('schedule.dressCode.detailTheme')}</p>
                      
                      <p className="font-semibold">{t('schedule.dressCode.detailGuidelines')}</p>
                      
                      <p>{t('schedule.dressCode.detailColorPalette')}</p>
                      
                      <p>{t('schedule.dressCode.detailLadies')}</p>
                      
                      <p>{t('schedule.dressCode.detailGentlemen')}</p>
                      
                      <p className="italic">{t('schedule.dressCode.detailFlexibility')}</p>
                      
                      <p className="font-semibold text-center mt-4">{t('schedule.dressCode.detailClosing')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tuesday - Farewell Brunch */}
            <div className="border-2 border-primary cream-bg p-6 md:p-8">
              <div className="flex items-start gap-4 mb-4">
                <Calendar className="w-6 h-6 blue-text flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h3 className="font-serif text-2xl font-semibold blue-text mb-2">
                    {t('schedule.dressCode.tuesday')}
                  </h3>
                  <p className="font-sans text-base blue-text opacity-80 mb-3">
                    {t('schedule.dressCode.tuesdayDesc')}
                  </p>
                  <div className="inline-block px-4 py-2 border-2 border-primary cream-bg">
                    <p className="font-sans font-semibold blue-text text-sm">
                      {t('schedule.dressCode.tuesdayCode')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};
